// Game textures (Poly Haven CC0, packed by tools/assets/*.mjs): loaded once with progress,
// mipmapped and anisotropically filtered. See tools/assets/polyhaven.mjs for the channel packing.
import * as THREE from 'three/webgpu';

const urls = import.meta.glob('../assets/textures/*.webp', { eager: true, query: '?url', import: 'default' }) as Record<string, string>;

export type TextureName =
  | 'gravel_road'
  | 'gravel_loose'
  | 'tarmac'
  | 'mud'
  | 'forest_floor'
  | 'grass'
  | 'rock';

export interface PbrSet {
  /** sRGB albedo (AO baked in). */
  albedo: THREE.Texture;
  /** Linear: RG = tangent normal XY, B = roughness. */
  nrm: THREE.Texture;
}

export interface GameTextures {
  sets: Record<TextureName, PbrSet>;
  spruceBranch: THREE.Texture;
  grassClump: THREE.Texture;
  fern: THREE.Texture;
}

const SET_NAMES: TextureName[] = ['gravel_road', 'gravel_loose', 'tarmac', 'mud', 'forest_floor', 'grass', 'rock'];

function url(file: string): string {
  const u = urls[`../assets/textures/${file}`];
  if (!u) throw new Error(`texture missing: ${file}`);
  return u;
}

let cache: Promise<GameTextures> | null = null;

/** Load every game texture (retrying each up to 3 times); `onProgress` gets 0..1. */
export function loadGameTextures(anisotropy: number, onProgress?: (f: number) => void): Promise<GameTextures> {
  if (cache) return cache;
  const loader = new THREE.TextureLoader();
  const files: string[] = [...SET_NAMES.flatMap((n) => [`${n}_albedo.webp`, `${n}_nrm.webp`]), 'spruce_branch_card.webp', 'grass_clump_card.webp', 'fern_card.webp'];
  let done = 0;
  const load = async (file: string, srgb: boolean): Promise<THREE.Texture> => {
    let last: unknown;
    for (let i = 0; i < 3; i++) {
      try {
        const t = await loader.loadAsync(url(file));
        t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        t.wrapS = t.wrapT = THREE.RepeatWrapping;
        t.anisotropy = anisotropy;
        t.generateMipmaps = true;
        t.minFilter = THREE.LinearMipmapLinearFilter;
        t.magFilter = THREE.LinearFilter;
        t.needsUpdate = true;
        onProgress?.(++done / files.length);
        return t;
      } catch (e) {
        last = e;
        await new Promise((r) => setTimeout(r, 300 * (i + 1)));
      }
    }
    throw new Error(`failed to load ${file}: ${String((last as Error)?.message ?? last)}`);
  };
  cache = (async () => {
    const entries = await Promise.all(SET_NAMES.map(async (n) => [n, { albedo: await load(`${n}_albedo.webp`, true), nrm: await load(`${n}_nrm.webp`, false) }] as const));
    const sets = Object.fromEntries(entries) as Record<TextureName, PbrSet>;
    const [spruceBranch, grassClump, fern] = await Promise.all([load('spruce_branch_card.webp', true), load('grass_clump_card.webp', true), load('fern_card.webp', true)]);
    for (const t of [spruceBranch, grassClump, fern]) t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    return { sets, spruceBranch, grassClump, fern };
  })();
  return cache;
}
